export type OPFSPath = string[];

export enum FileOP {
    Close,
    Flush,
    Open,
    Read,
    Truncate,
    Write,
    Error,
}

export interface OPFSWorkerBaseMessage {
    uid?: number;
    op: FileOP;
}

export interface OPFSWorkerOpenMessage extends OPFSWorkerBaseMessage {
    op: FileOP.Open;
    path: OPFSPath;
}

export interface OPFSWorkerCloseMessage extends OPFSWorkerBaseMessage {
    op: FileOP.Close;
    fd: number;
}

export interface OPFSWorkerFlushMessage extends OPFSWorkerBaseMessage {
    op: FileOP.Flush;
    fd: number;
}

export interface OPFSWorkerWriteMessage extends OPFSWorkerBaseMessage {
    op: FileOP.Write;
    fd: number;
    buffer: AllowSharedBufferSource;
    at: number;
}

export interface OPFSWorkerTruncateMessage extends OPFSWorkerBaseMessage {
    op: FileOP.Truncate;
    fd: number;
    newSize: number;
}

export interface OPFSWorkerReadMessage extends OPFSWorkerBaseMessage {
    op: FileOP.Read;
    fd: number;
    at: number;
    count: number;
}

export type OPFSWorkerMessage =
    | OPFSWorkerOpenMessage
    | OPFSWorkerCloseMessage
    | OPFSWorkerFlushMessage
    | OPFSWorkerWriteMessage
    | OPFSWorkerTruncateMessage
    | OPFSWorkerReadMessage;

export interface OPFSWorkerBaseResult {
    uid: number;
    op: FileOP;
}

export interface OPFSWorkerOpenResult extends OPFSWorkerBaseResult {
    op: FileOP.Open;
    fd: number;
}

export interface OPFSWorkerCloseResult extends OPFSWorkerBaseResult {
    op: FileOP.Close;
}

export interface OPFSWorkerFlushResult extends OPFSWorkerBaseResult {
    op: FileOP.Flush;
}

export interface OPFSWorkerWriteResult extends OPFSWorkerBaseResult {
    op: FileOP.Write;
    count: number;
}

export interface OPFSWorkerTruncateResult extends OPFSWorkerBaseResult {
    op: FileOP.Truncate;
}

export interface OPFSWorkerReadResult extends OPFSWorkerBaseResult {
    op: FileOP.Read;
    buffer: ArrayBuffer;
    count: number;
}

export interface OPFSWorkerErrorResult extends OPFSWorkerBaseResult {
    op: FileOP.Error;
    err: Error;
}

export type OPFSWorkerResult =
    | OPFSWorkerOpenResult
    | OPFSWorkerCloseResult
    | OPFSWorkerFlushResult
    | OPFSWorkerWriteResult
    | OPFSWorkerTruncateResult
    | OPFSWorkerReadResult
    | OPFSWorkerErrorResult;

export type OPFSWorkerResultFor<T extends OPFSWorkerMessage> = T extends OPFSWorkerOpenMessage ? OPFSWorkerOpenResult
    : T extends OPFSWorkerCloseMessage ? OPFSWorkerCloseResult
    : T extends OPFSWorkerFlushMessage ? OPFSWorkerFlushResult
    : T extends OPFSWorkerReadMessage ? OPFSWorkerReadResult
    : T extends OPFSWorkerTruncateMessage ? OPFSWorkerTruncateResult
    : T extends OPFSWorkerWriteMessage ? OPFSWorkerWriteResult
    : OPFSWorkerResult;

export const getDirectoryHandler = async (pathParts: OPFSPath, createDir: boolean = false) => {
    const root = await globalThis.navigator.storage.getDirectory();
    let dir = root;
    let idx = 0;
    while (idx < pathParts.length) {
        dir = await dir.getDirectoryHandle(pathParts[idx], { create: createDir });
        idx++;
    }
    return dir;
};

export const getFileHandler = async (path: OPFSPath, createDir: boolean = false, createFile: boolean = false) => {
    const dir = await getDirectoryHandler(path.slice(0, -1), createDir);
    return await dir.getFileHandle(path[path.length - 1], { create: createFile });
};

export const getParentDirectoryHandler = async (handler: FileSystemHandle) => {
    const path = await resolvePath(handler);
    return getDirectoryHandler(path.slice(0, -1));
};

export const removeEntry = async (handler: FileSystemHandle) => {
    const parent = await getParentDirectoryHandler(handler);
    if (await parent.isSameEntry(handler)) return; // can not remove /
    await parent.removeEntry(handler.name, { recursive: true });
};

export const resolvePath = async (handler: FileSystemHandle) => {
    return await (await globalThis.navigator.storage.getDirectory()).resolve(handler) as OPFSPath;
};
